import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigurationService } from '../configuration/configuration.service';
import * as apiConfig from '../../config/api_json.json';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigurationService);
  
  /**
   * Get API configuration from centralized config
   * @param key - API configuration key
   * @returns The API configuration object
   */
  private getApiConfig(key: string): any {
    return apiConfig[key as keyof typeof apiConfig];
  }
  
  /**
   * Get URL for an API endpoint from config
   * @param key - API configuration key
   * @returns The API URL
   */
  getApiUrlFromConfig(key: string): string {
    const config = this.getApiConfig(key);
    return config?.url || '';
  }
  
  /**
   * Get HTTP method for an API endpoint from config
   * @param key - API configuration key
   * @returns The HTTP method
   */
  getApiMethodFromConfig(key: string): string {
    const config = this.getApiConfig(key);
    return config?.method || 'GET';
  }
  
  /**
   * API endpoint templates for different API types
   */
  private readonly API_TEMPLATES = {
    COMMON: 'common',
    URF: 'urf'
  } as const;

  /**
   * Get service template for an API endpoint from config
   * @param key - API configuration key
   * @returns The service template
   */
  getApiServiceFromConfig(key: string): 'COMMON' | 'URF' {
    const config = this.getApiConfig(key);
    const service = config?.service || 'COMMON';
    return service as 'COMMON' | 'URF';
  }

  /**
   * Get the full API URL for a given endpoint with template support
   * @param endpoint - The API endpoint path
   * @param template - The API template type ('common' or 'urf')
   * @param customBaseUrl - Optional custom base URL to override defaults
   */
  private getApiUrl(endpoint: string, template: 'COMMON' | 'URF' = 'COMMON', customBaseUrl?: string): string {
    if (customBaseUrl) {
      return `${customBaseUrl}${endpoint}`;
    }

    let baseUrl: string;
    
    switch (template) {
      case 'URF':
        baseUrl = this.configService.getUrfApiBaseUrl() || 'https://api.onerims-dev.freyrapps.com';
        break;
      case 'COMMON':
      default:
        baseUrl = this.configService.getApiBaseUrl();
        break;
    }
    
    return `${baseUrl}${endpoint}`;
  }

  /**
   * Get HTTP headers with bearer token
   */
  private getHeaders(token?: string): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Generic GET request
   * @param endpoint - The API endpoint path
   * @param token - Optional bearer token
   * @param params - Optional HTTP parameters
   * @param template - API template type ('COMMON' or 'URF')
   * @param customBaseUrl - Optional custom base URL
   * @param responseType - Optional response type ('json', 'text', 'blob', 'arraybuffer')
   */
  get<T>(
    endpoint: string, 
    token?: string, 
    params?: HttpParams, 
    template: 'COMMON' | 'URF' = 'COMMON', 
    customBaseUrl?: string,
    responseType?: 'json' | 'text' | 'blob' | 'arraybuffer'
  ): Observable<T> {
    const url = this.getApiUrl(endpoint, template, customBaseUrl);
    const headers = this.getHeaders(token);
    
    if (responseType && responseType !== 'json') {
      return this.http.get(url, { headers, params, responseType: responseType as any }) as Observable<T>;
    }
    
    return this.http.get<T>(url, { headers, params });
  }

  /**
   * Generic POST request
   * @param endpoint - The API endpoint path
   * @param data - Request body data
   * @param token - Optional bearer token
   * @param template - API template type ('COMMON' or 'URF')
   * @param customBaseUrl - Optional custom base URL
   * @param responseType - Optional response type ('json', 'text', 'blob', 'arraybuffer')
   */
  post<T>(
    endpoint: string, 
    data: unknown, 
    token?: string, 
    template: 'COMMON' | 'URF' = 'COMMON', 
    customBaseUrl?: string,
    responseType?: 'json' | 'text' | 'blob' | 'arraybuffer'
  ): Observable<T> {
    const url = this.getApiUrl(endpoint, template, customBaseUrl);
    const headers = this.getHeaders(token);
    
    if (responseType && responseType !== 'json') {
      return this.http.post(url, data, { headers, responseType: responseType as any }) as Observable<T>;
    }
    
    return this.http.post<T>(url, data, { headers });
  }

  /**
   * Generic PUT request
   * @param endpoint - The API endpoint path
   * @param data - Request body data
   * @param token - Optional bearer token
   * @param template - API template type ('COMMON' or 'URF')
   */
  put<T>(endpoint: string, data: unknown, token?: string, template: 'COMMON' | 'URF' = 'COMMON'): Observable<T> {
    const url = this.getApiUrl(endpoint, template);
    const headers = this.getHeaders(token);
    return this.http.put<T>(url, data, { headers });
  }

  /**
   * Generic DELETE request
   * @param endpoint - The API endpoint path
   * @param token - Optional bearer token
   * @param template - API template type ('COMMON' or 'URF')
   */
  delete<T>(endpoint: string, token?: string, template: 'COMMON' | 'URF' = 'COMMON'): Observable<T> {
    const url = this.getApiUrl(endpoint, template);
    const headers = this.getHeaders(token);
    return this.http.delete<T>(url, { headers });
  }

  /**
   * Generic PATCH request
   * @param endpoint - The API endpoint path
   * @param data - Request body data
   * @param token - Optional bearer token
   * @param template - API template type ('COMMON' or 'URF')
   */
  patch<T>(endpoint: string, data: unknown, token?: string, template: 'COMMON' | 'URF' = 'COMMON'): Observable<T> {
    const url = this.getApiUrl(endpoint, template);
    const headers = this.getHeaders(token);
    return this.http.patch<T>(url, data, { headers });
  }
}
